import React from "react";
import { data, useNavigate } from "react-router-dom";
import type { JSX } from "react";
import type { ProductData } from "@/data/products";
import { Carousel } from "primereact/carousel";
import { SplitButton } from "primereact/splitbutton";
import { type MenuItem } from "primereact/menuitem";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { type ProductShowcaseType, AllProducts } from "@/data/products";
import { Tree } from "primereact/tree";
import { type TreeNode } from "primereact/treenode";

function ProductCard(image: string): JSX.Element {
    return (
        <div className="w-full flex justify-center items-center">
            <img className="w-md rounded-2xl" src={image} />
        </div>
    )
}

function OtherProductsCard(product: ProductShowcaseType): JSX.Element {
    const navigate = useNavigate()

    return (
        <div className="w-fit flex flex-col justify-center items-center p-2 cursor-pointer" onClick={() => navigate(product.url)}>
            <img className="rounded-2xl" src={product.image} />
            <div className="text-2xl font-bold">{ product.name }</div>
        </div>
    )
}

export function Product({ details }: { details: ProductData }): JSX.Element {
   const [ buyLinks, setBuyLinks ] = React.useState<MenuItem[]>();
    const [width, setWidth] = React.useState(window.innerWidth);

   React.useEffect(() => {
        let buyLinks: MenuItem[] = details.buyLinks.map(
            (value: {name: string; url: string;}) => ({
                label: value.name,
                command: () => {
                    window.open(value.url, "_blank");
                }
            })
        );
        setBuyLinks(buyLinks);
    }, [details.buyLinks])

    React.useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    return (
        <div className="px-4 w-full h-fit flex flex-col items-center gap-10">
            {/* spacer to move context below navbar */}
            <div className="h-10 md:h-16"></div>

            {/* Top Card to display Images & Basic Infos */}
            <div className="card w-full px-4 grid grid-flow-row xl:grid-cols-2">
                {/* Images */}
                <div className="h-fit row-span-1 xl:col-span-1 pt-4">
                    <Carousel
                        value={details.images}
                        numVisible={1}
                        numScroll={1}
                        itemTemplate={ProductCard}
                    />
                </div>

                {/* Pirce, Descriptions & Buy options*/}
                <div className="py-8 xl:px-8 row-span-1 xl:col-span-1 xl:h-full w-full grid grid-flow-row gap-5 xl:gap-0">
                    {/* Titile, Price & Description */}
                    <div className="row-span-1 flex flex-col gap-2 xl:gap-5">
                        <div className="text-4xl !font-bold sm:text-6xl">
                            Qubee {details.title}
                        </div>

                        <div className="text-3xl sm:text-5xl">
                            Rs. {details.price} 
                        </div>

                        <div className="pt-3 text-md sm:text-lg">
                            {details.description}
                        </div>
                    </div>

                    {/* Buy Options */}
                    <div className="h-fit xl:h-full row-spans-2 flex flex-col justify-start xl:justify-end items-start">
                        <SplitButton
                            size="large"
                            label="Buy"
                            icon="pi pi-shopping-cart"
                            model={buyLinks}
                            disabled={details.buyLinks.length == 0}
                            onClick={() => {
                                window.open(details.buyLinks[0]?.url, "_blank");
                            }
                            } 
                        />
                    </div>
                </div>
            </div>

            <div className="w-full flex flex-col xl:flex-row justify-evenly">
                <div className="card h-fit w-full xl:w-fit overflow-clip">
                        <DataTable
                            value={details.datas}
                            stripedRows
                            showGridlines
                            size={ width > 840 ? "normal" : "small" }
                            tableStyle={{ minWidth: '50rem' }}
                        >
                            <Column field="name" header="Category"></Column>
                            <Column field="description" header="Description"></Column>
                        </DataTable>
                </div>

                <div className="h-fit w-full xl:w-fit card flex justify-center items-center flex-col">
                    <div className="pt-8 text-3xl">
                        Other products
                    </div>

                    <div className="w-sm">
                        <Carousel
                            value={AllProducts.filter(((value: ProductShowcaseType) => {
                                return value.name != details.title;
                            }))}
                            numVisible={1}
                            numScroll={1}
                            itemTemplate={OtherProductsCard}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
